import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import companiesService, { Company, CompanyInput } from '../../../services/crm/companiesService';
import { useCrmOrgStore } from '../../../store/crmOrg.store';

type ViewMode = 'table' | 'kanban' | 'gallery';

const CompaniesTab: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [items, setItems] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  
  // Simplified form state
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const quickAddRef = useRef<HTMLInputElement | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const { data } = await companiesService.list({
        organization_id: activeOrgId,
        limit: 100,
        offset: 0,
        search: search.trim() || undefined
      });
      
      setItems(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, search]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const createCompany = useCallback(async () => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    if (!name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setLoading(true);
    try {
      const payload: CompanyInput = {
        organization_id: activeOrgId,
        name: name.trim(),
        industry: industry.trim() || undefined,
        website: website.trim() || undefined
      };

      await companiesService.create(payload);
      setName('');
      setIndustry('');
      setWebsite('');
      toast.success('Company added');
      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, name, industry, website, fetchData]);


  return (
    <div className="space-y-6">
      {/* Filters & View Switcher */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <input
          type="text"
          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Search companies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        
        {/* View Mode Switcher */}
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Table view"
          >
            📋
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'kanban' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Kanban view"
          >
            📊
          </button>
          <button
            onClick={() => setViewMode('gallery')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'gallery' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Gallery view"
          >
            🎴
          </button>
        </div>
        <Link
          to="/help?article=crm-companies"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
        >
          ❓ Companies help
        </Link>
      </div>

      {/* Compact Quick Add Form */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">➕ Quick Add Company</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <input
            ref={quickAddRef}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Company name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <button
            onClick={() => void createCompany()}
            disabled={loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'table' && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {search ? 'No companies match your filters.' : 'No companies yet. Add one above!'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Industry</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Website</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((company) => (
                    <tr key={company.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-primary">{company.name}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{company.industry || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {company.website ? (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                            Link
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/crm/companies/${company.id}`)}
                          className="rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'kanban' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((company) => (
            <div
              key={company.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md"
              onClick={() => navigate(`/crm/companies/${company.id}`)}
            >
              <div className="mb-2 text-2xl">🏢</div>
              <div className="font-semibold text-gray-900">{company.name}</div>
              {company.industry && <div className="mt-1 text-xs text-gray-500">{company.industry}</div>}
            </div>
          ))}
        </div>
      )}

      {viewMode === 'gallery' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((company) => (
            <div
              key={company.id}
              className="cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              onClick={() => navigate(`/crm/companies/${company.id}`)}
            >
              <div className="mb-2 text-2xl">🏢</div>
              <h4 className="mb-1 font-semibold text-gray-900">{company.name}</h4>
              <p className="text-xs text-gray-500">{company.industry || 'No industry'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompaniesTab;
